// 剣舞の絵の新旧 (2026-10-19)。
//
// 配布版の既定 = 2026-10-18 に作者が「好き」と答えた B の立ち絵 (顔の無い藍の体・刃の冠・核なし・左向き)。
//   6つのキー (idle / telegraph / active / parried / staggered / defeat) は全部この1枚を読む
//   (sprites.ts SPRITE_FILE_OVERRIDE で 5キー → idle)。技ごとの絵は下の比較版 (?bladeMoves=1) で動きをつないでいる途中。
//   大きさ = 主人公 120 × 1.55 = 186px (render.ts BOSS_HEIGHT_RATIO)・縮尺は花嫁と同じ固定式 (sprites.ts BOSS_FIXED_SCALE_REF_H 1437)。
//   部屋 = C 金の舞台 (render.ts BG_SRC_BY_BOSS '/bg/blade-dancer-stage.png')。
//   記録 = docs/research/2026-10-19-blade-dancer-st0-ship.md
//
// 旧 (E-5 の赤いドレスの6枚・右向き・1.25 倍・面積で縮尺・城の背景) は DEV の ?bladeArt=old で戻る (main.ts の DEV の箱が呼ぶ)。
//   旧の6枚は public/sprites/bosses/blade-dancer/_archived-2026-10-19-e5-old/ (dist からは落ちる = 開発版だけで見える)。
import { setBossFixedScaleRefH, setBossNoFlinch, setBossTimelineCrossfade, setSpriteFileOverride, SOURCE_FACING } from './sprites'
import type { AttackDef, SpriteKey } from './types'
import { ATTACKS } from './attacks'
import { BOSS_ATTACK_TIMELINES, type BossAttackTimeline } from './boss_pose_timeline'
import { BG_SRC_BY_BOSS, CONTACT_SMEAR_BOSSES, SMEAR_ARTS, preloadBladePetals, setBossHeightRatioOverride, type ContactSmearArc } from './render'

const BOSS_ID = 'blade-dancer'
export const BLADE_DANCER_OLD_ART_DIR = '_archived-2026-10-19-e5-old'
export const BLADE_DANCER_OLD_HEIGHT_RATIO = 1.25
const KEYS: SpriteKey[] = ['idle', 'telegraph', 'active', 'parried', 'staggered', 'defeat']

/** 剣舞を旧の絵・向き・大きさ・部屋へ戻す (DEV の ?bladeArt=old だけが呼ぶ)。 */
export function useBladeDancerOldArt(): void {
  for (const key of KEYS) setSpriteFileOverride(BOSS_ID, '', key, `${BLADE_DANCER_OLD_ART_DIR}/${key}`)
  SOURCE_FACING[BOSS_ID] = 'right'
  setBossFixedScaleRefH(BOSS_ID, null)
  setBossHeightRatioOverride(BOSS_ID, BLADE_DANCER_OLD_HEIGHT_RATIO)
  delete BG_SRC_BY_BOSS[BOSS_ID]
}

// 技ごとの絵の比較版 (2026-10-19・DEV ?bladeMoves=1)。B の立ち絵を見本に Codex が姿勢だけ描き直した絵で、技の各場面を出し分ける。
//   配布版は今まで通り全部 idle (ここは誰も呼ばない)。技の流れが遊べる形になるまで、合否は測れる物 (絵が替わる時の頭と足の飛び) で決める。
//   起き上がり (hem_rise)・突きの戻り (thrust_recover)・滑り寄り (glide)・回りながら突きへ入る所 (pirouette_turn) は、
//   絵が替わる時の飛びを詰めるための間の絵。
//   当たる瞬間は全部 active の頭 (at 0) = 今の collisionDraft と同じで、時間は1ミリ秒も変えない。
//   当たりの前後の届きだけは、当たる瞬間の絵の剣先に合わせる (下の BLADE_DANCER_MOVE_ART_FRONT_PX)。
//   絵 = public/sprites/bosses/blade-dancer/<key>.png (原画の縮尺を idle にそろえて置く)。
//   記録 = docs/research/2026-10-19-blade-dancer-move-art.md
export const BLADE_DANCER_MOVE_TIMELINES: Record<string, BossAttackTimeline> = {
  // 円舞連斬 (3連): 構え → 斬り下ろし／横薙ぎ／斬り下ろし → 剣を差し替えて立ち姿へ。
  waltzFlurry: [
    { phase: 'telegraph', at: 0, image: 'waltz_ready', hold: true, tag: 'anticipation' },
    { phase: 'active', at: 0, image: 'waltz_cut1', hold: true, tag: 'contact', hitIndex: 0 },
    { phase: 'active', at: 0, image: 'waltz_cut2b', hold: true, tag: 'contact', hitIndex: 1 },
    { phase: 'active', at: 0, image: 'waltz_cut1', hold: true, tag: 'contact', hitIndex: 2 },
    { phase: 'recovery', at: 0, image: 'swap_draw', hold: true, tag: 'recover' },
    { phase: 'recovery', at: 0.7, image: 'idle', hold: true },
  ],
  // 旋回突き (赤・弾けない): 背を向けて溜める → 回りながら腰を落とす → 一直線の突き → 突いたまま止まる → 後ろ足を引き寄せる → 立ち姿へ。
  //   溜めから突きへ直に替えると、当たる瞬間に頭が 39px 下・後ろ足が 107px 後ろへ跳ぶので、溜めの終わりに回る絵を挟む。
  //   戻りの絵は前へ少し出してから戻し (前足の飛びを詰める)、立ち姿は縦に縮めた所から伸ばす (頭の段差を詰める)。
  pirouette: [
    { phase: 'telegraph', at: 0, image: 'pirouette_wind', hold: true, tag: 'anticipation' },
    { phase: 'telegraph', at: 0.78, image: 'pirouette_turn', hold: true },
    { phase: 'active', at: 0, image: 'pirouette_thrust', hold: true, tag: 'contact' },
    { phase: 'recovery', at: 0, image: 'pirouette_thrust', hold: true, tag: 'followThrough', ease: 'step' },
    { phase: 'recovery', at: 0.3, image: 'thrust_recover', hold: true, tag: 'recover', offset: { x: 20 }, ease: 'easeOut' },
    { phase: 'recovery', at: 0.55, image: 'thrust_recover', hold: true, ease: 'step' },
    { phase: 'recovery', at: 0.6, image: 'idle', hold: true, scale: { y: 0.82 }, ease: 'easeOut' },
    { phase: 'recovery', at: 0.85, image: 'idle', hold: true },
  ],
  // 裾払い: 構え (円舞と同じ) → 沈みながら腰を落とす → 低く回って床を払う → 起き上がる → 立ち姿へ。
  //   頭の高さ (画面 px) = 構え 138 → 起き上がり 100 → 裾払い 66。構えと立ち姿の側は縦に縮めて段差を詰める
  //   (縮みは足元を軸にするので足は動かない)。
  hemSweep: [
    { phase: 'telegraph', at: 0, image: 'waltz_ready', hold: true, tag: 'anticipation', ease: 'easeIn' },
    { phase: 'telegraph', at: 0.55, image: 'waltz_ready', hold: true, scale: { y: 0.85 }, ease: 'step' },
    { phase: 'telegraph', at: 0.6, image: 'hem_rise', hold: true },
    { phase: 'active', at: 0, image: 'hem_sweep', hold: true, tag: 'contact' },
    { phase: 'recovery', at: 0, image: 'hem_sweep', hold: true, tag: 'followThrough' },
    { phase: 'recovery', at: 0.35, image: 'hem_rise', hold: true, tag: 'recover', ease: 'step' },
    { phase: 'recovery', at: 0.7, image: 'idle', hold: true, scale: { y: 0.82 }, offset: { x: 15 }, ease: 'easeOut' },
    { phase: 'recovery', at: 0.95, image: 'idle', hold: true },
  ],
  // 終舞・五連: 構え → 斬り下ろしと横薙ぎを交互に4発 → 5発目は大斬り → 剣を差し替えて立ち姿へ。
  crescendo: [
    { phase: 'telegraph', at: 0, image: 'waltz_ready', hold: true, tag: 'anticipation' },
    { phase: 'active', at: 0, image: 'waltz_cut1', hold: true, tag: 'contact', hitIndex: 0 },
    { phase: 'active', at: 0, image: 'waltz_cut2b', hold: true, tag: 'contact', hitIndex: 1 },
    { phase: 'active', at: 0, image: 'waltz_cut1', hold: true, tag: 'contact', hitIndex: 2 },
    { phase: 'active', at: 0, image: 'waltz_cut2b', hold: true, tag: 'contact', hitIndex: 3 },
    { phase: 'active', at: 0, image: 'exec_slash', hold: true, tag: 'contact', hitIndex: 4 },
    { phase: 'recovery', at: 0, image: 'swap_draw', hold: true, tag: 'recover' },
    { phase: 'recovery', at: 0.7, image: 'idle', hold: true },
  ],
  // 幕引きの一閃 (ボスの致命): 構え → 片足のつま先で床を滑って寄る (致命の寄り = boss.ts の BOSS_EXECUTE_APPROACH_PX) →
  //   交差の大斬り → 振り抜いたまま止まる → 剣を差し替えて立ち姿へ。
  //   振り抜きから立ち姿へ直に戻すと頭が 48px 上へ跳ぶので、間に剣を差し替える絵を挟む。
  dancerExecution: [
    { phase: 'telegraph', at: 0, image: 'waltz_ready', hold: true, tag: 'anticipation', ease: 'easeIn' },
    { phase: 'telegraph', at: 0.12, image: 'waltz_ready', hold: true, scale: { y: 0.85 }, ease: 'step' },
    { phase: 'telegraph', at: 0.15, image: 'glide', hold: true },
    { phase: 'active', at: 0, image: 'exec_slash', hold: true, tag: 'contact' },
    { phase: 'recovery', at: 0, image: 'exec_slash', hold: true, tag: 'followThrough' },
    { phase: 'recovery', at: 0.6, image: 'swap_draw', hold: true, tag: 'recover' },
    { phase: 'recovery', at: 0.85, image: 'idle', hold: true },
  ],
}
// 状態の絵 (弾かれ・崩れ・撃破)。配布版は idle を向けている (sprites.ts SPRITE_FILE_OVERRIDE) のを、比較版だけ <key>_1019 へ向ける。
//   ファイル名を <key>_1019 にするのは、倍率と横の基準 (sprites.ts の剣舞の表) を幹の名前で引くため。
//   キー名で引くと、配布版で idle を読んでいる時にも新しい絵の倍率が掛かってしまう。
export const BLADE_DANCER_MOVE_STATE_KEYS: SpriteKey[] = ['parried', 'staggered', 'defeat']

// 当たりの前端を当たる瞬間の絵に合わせる (全ボス共通の「当たりは絵に合わせる」・花嫁と亡霊の src/bride_shade_reach.ts と同じ決め方)。
//   前端 = 主人公の背丈 (床から 120px) の内に描かれた剣の一番前 + REACH_FIT_MARGIN_PX (主人公の当たりの半幅 15)。
//   頭より上を通る刃は主人公に触れないので数えない。採寸 = 原画の不透明画素を置き方 (sprites.ts resolveBossSpritePlacement) と同じ縮尺で画面へ直した値。
//   後ろ側は今の控えの形 (hits.ts collisionDraft の -min(range, BOSS.rearHitRange 100)) のまま。
//   踏み込みの着地 (approachDistancePx - gapCloseBuffer 28) は前端の内側に来るよう、その技の前端の一番短い値にする。
//   裾払いは近接の当たりが無く、床の帯 (hazardSpawns・b+85〜215) が当たり。帯は霞の絵 (render.ts poison_mist) が判定の幅に伸びて描かれる = 届きは帯の絵が見せている。
export const BLADE_DANCER_MOVE_ART_FRONT_PX = {
  waltz_cut1: 127,
  waltz_cut2b: 160,
  pirouette_thrust: 157,
  exec_slash: 157,
} as const
const REACH_FIT_MARGIN_PX = 15
const REAR_PX = -100
const MOVE_STRIKE_IMAGES: Record<string, (keyof typeof BLADE_DANCER_MOVE_ART_FRONT_PX)[]> = {
  waltzFlurry: ['waltz_cut1', 'waltz_cut2b', 'waltz_cut1'],
  pirouette: ['pirouette_thrust'],
  crescendo: ['waltz_cut1', 'waltz_cut2b', 'waltz_cut1', 'waltz_cut2b', 'exec_slash'],
  dancerExecution: ['exec_slash'],
}

// 控えて戻す欄 = 届き (range・approach・strike・段ごとの当たり) と、v2 で替える移動の仕方 (踏み込み・溜めの間の寄り・連撃の間の寄り)。
const SNAP_FIELDS = ['chargeMs', 'range', 'approachDistancePx', 'strike', 'comboStages', 'lungeDistance', 'preActiveLungeMs', 'preActiveLungeDistance', 'preActiveLungeStopGapPx', 'preActiveLungeMinPx', 'preActiveLungeEase', 'comboLunge', 'gapCloseBuffer', 'recoveryMs'] as const
type ReachSnap = Pick<AttackDef, typeof SNAP_FIELDS[number]>
let reachSnap: Record<string, ReachSnap> | null = null
/** 技ごとの当たる瞬間の絵の前端 (画面 px) の並びから、届き・踏み込みの着地・連撃の段ごとの当たりを決める。 */
function applyArtReach(frontsByAttack: Record<string, readonly number[]>): void {
  reachSnap ??= {}
  for (const id of Object.keys(frontsByAttack)) {
    if (reachSnap[id]) continue
    const a = ATTACKS[id]!
    reachSnap[id] = Object.fromEntries(SNAP_FIELDS.map(f => [f, a[f]])) as ReachSnap
  }
  for (const [id, artFronts] of Object.entries(frontsByAttack)) {
    const a = ATTACKS[id]!
    const fronts = artFronts.map(px => px + REACH_FIT_MARGIN_PX)
    a.range = Math.max(...fronts)
    a.approachDistancePx = Math.min(...fronts)
    a.strike = { localFromPx: REAR_PX, localToPx: fronts[0]! }
    if (fronts.length > 1) a.comboStages = fronts.map(to => ({ strike: { localFromPx: REAR_PX, localToPx: to } }))
  }
}
function applyMoveArtReach(): void {
  applyArtReach(Object.fromEntries(Object.entries(MOVE_STRIKE_IMAGES)
    .map(([id, images]) => [id, images.map(k => BLADE_DANCER_MOVE_ART_FRONT_PX[k])])))
}
/** 当たりを attacks.ts の値へ戻す (テストの後始末用)。 */
export function restoreBladeDancerMoveReach(): void {
  if (!reachSnap) return
  for (const [id, s] of Object.entries(reachSnap)) {
    const a = ATTACKS[id]! as unknown as Record<string, unknown>
    for (const f of SNAP_FIELDS) {
      if (s[f] === undefined) delete a[f]
      else a[f] = s[f]
    }
  }
}

/** 剣舞の技ごとの絵を出す (DEV の ?bladeMoves=1 だけが呼ぶ・preloadBossSprites より前に呼ぶ)。当たりの前端も絵に合わせる。 */
export function useBladeDancerMoveArt(): void {
  BOSS_ATTACK_TIMELINES[BOSS_ID] = BLADE_DANCER_MOVE_TIMELINES
  for (const key of BLADE_DANCER_MOVE_STATE_KEYS) setSpriteFileOverride(BOSS_ID, '', key, `${key}_1019`)
  applyMoveArtReach()
}

// 動きを1から作った版 (2026-10-20・DEV ?bladeMotion=v2)。円舞連斬 (3連) と、同じ絵を使い回す終舞・五連だけを差し替える。
//   ほかの技・弾かれ・崩れ・撃破は配布版のまま (立ち絵)。旧の比較版 (?bladeMoves=1) の絵は1枚も使わない。
//   絵 = Codex が1枚の紙にひと振りの4コマを続けて描いた3枚 (そざい/concept/blade-dancer-motion-1020/) から切り出した12コマ。
//     1〜4 = 斬り下ろし (振りかぶり → 振り下ろしの途中 → 当たる瞬間 → 振り抜き)
//     5〜8 = 回って横一文字 (背中を見せて回る → 回り終わり・剣が後ろから回ってくる → 当たる瞬間 → 振り抜き)
//     9〜12 = 回って斬り上げ (二度目の回転 → 当たる瞬間・剣が前下から上がる途中 → 上がり切り・つま先で一番高い → 見得)
//   決め方:
//     ・連撃の間 (comboGap) で、振り抜き → 回る → 次の振り出し と絵を進める = 前の当たる絵のまま止まらない。
//     ・当たる直前の絵 (2・6・9) は、どの発でもおよそ 85〜120ms 見せる = 弾く拍が絵で読める (円舞は3拍子の等間隔)。
//     ・溜め (10-20 磨き) = 沈み込み w1 → 振り上げの途中 w2 → 頂点 w3 → 踏み出す 2。3コマは別の紙 (waltz_windup.png) に続けて描かせた。
//       頂点は 1 でなく w3 (沈み込み・振り上げと同じ紙で、背丈がそろう)。1 は使っていない (残してある)。
//   時間 (溜め・当たり・間・戻り) は attacks.ts のまま。当たりの前端だけ当たる瞬間の絵の剣先に合わせる。
//   記録 = docs/research/2026-10-20-blade-dancer-motion-from-scratch.md
const W = (n: number) => `m1020_waltz_${n}`
const WU = (n: number) => `m1020_waltz_w${n}`   // 溜めの3コマ
const SU = (n: number) => `m1020_waltz_s${n}`   // 見得から立ち姿へ戻る2コマ (剣を下ろす途中・立ち姿の手前)
const LOWER = 'm1020_waltz_c1'   // 振り抜き 11 から見得 12 へ降りる途中 (前の剣が真上・上げた脚が床の手前)
const LAUNCH = 'm1020_waltz_c2'  // 振りかぶり w3 から踏み込み 2 へ出る途中 (前の剣が肩の高さで水平に前・前の脚が床の手前)
const RAISE = 'm1020_waltz_c4'   // 溜めの w1 から w2 へ剣を上げる途中 (前の剣が胸の前から斜め前上へ・膝が伸び始める)
const SPIN_AGAIN = 'm1020_waltz_c5' // 振り抜き 8 から二度目の回転 9 へ伸び上がる途中 (つま先立ちで腰が上がり、前の剣が体の前を横切る)
const STAND = 'm1020_waltz_c6'      // 戻りの s2 から立ち姿へ膝を伸ばしていく途中 (剣は下ろしたまま・背丈が s2 と立ち姿の真ん中)
const SPIN_IN = 'm1020_waltz_c3' // 振り抜き 4 から回る 5 へ移る途中 (腰が浮き、前の脚で床を押して回り始め、胸がこちらを向く・剣は腰の高さで横に寝る)
// 1枚ごとの「生きた動き」(offset/scale/rot・支点は足元・描画だけ)。絵はぱっと替わり、替わったその1枚が動く。
//   止め絵のまま 100ms 以上動かない所を作らない (等速で見ると人形に見えるため)。量は小さく (拡縮 5% まで・傾き 4° まで)。
const DIP = { scale: { x: 1.02, y: 0.97 } }                                // 沈み込みの底へもう一段沈む
const COIL = { offset: { x: -10 }, scale: { x: 0.98, y: 1.04 }, rot: -4 }   // 振りかぶりの頂点へ伸び上がって反る
const LEAN = { rot: 3 }                                                    // 踏み出して前へ倒れ込む
const DRIVE = { offset: { x: 6 }, rot: 2 }                                 // 振りかぶりから前へ出始める
const HIT = { scale: { x: 1.05, y: 0.95 }, rot: 2 }                         // 当たる瞬間に沈む → 間で戻る
const TURN = { scale: { x: 0.97, y: 1.03 } }                               // 回りながら伸び上がる
const READY = { rot: 2 }                                                   // 次の振りの直前に前へ乗る
const POSE = { scale: { y: 1.03 }, rot: -2 }                               // 見得: つま先で伸び上がる
const RISE = { scale: { x: 0.99, y: 1.02 } }                                // 沈んだ底から振りかぶりへ伸び上がり始める
const CARRY = { offset: { x: 4 }, scale: { x: 0.98, y: 1.03 }, rot: -2 }     // 斬り上げを振り抜いた勢いで伸び上がる (振り抜いた絵が止まらない)
const SETTLE_HI = { scale: { y: 1.015 } }                                  // 剣を下ろしながら、まだつま先に残る
const SETTLE_LO = { scale: { x: 1.01, y: 0.985 } }                         // かかとが降りて立ち姿へ収まる
export const BLADE_DANCER_MOTION_V2_TIMELINES: Record<string, BossAttackTimeline> = {
  waltzFlurry: [
    { phase: 'telegraph', at: 0, image: SU(2), hold: true, tag: 'anticipation', ease: 'easeOut', ...SETTLE_LO },   // 立ち姿から、戻りの絵を逆にたどって構えへ
    { phase: 'telegraph', at: 0.07, image: WU(1), hold: true, ease: 'easeOut' },
    { phase: 'telegraph', at: 0.14, image: WU(1), hold: true, ease: 'easeOut', ...DIP },
    { phase: 'telegraph', at: 0.17, image: RAISE, hold: true, ease: 'easeIn' },   // 沈みから剣を上げながら伸び上がる途中
    { phase: 'telegraph', at: 0.23, image: WU(2), hold: true, ease: 'easeIn' },
    { phase: 'telegraph', at: 0.38, image: WU(2), hold: true, ease: 'easeOut', ...RISE },
    { phase: 'telegraph', at: 0.4, image: WU(3), hold: true, ease: 'easeOut' },
    { phase: 'telegraph', at: 0.74, image: WU(3), hold: true, ease: 'easeIn', ...COIL },
    { phase: 'telegraph', at: 0.76, image: LAUNCH, hold: true, ease: 'easeIn', ...DRIVE },   // 剣を肩の高さで前へ出しながら踏み込みへ
    { phase: 'telegraph', at: 0.85, image: W(2), hold: true, ease: 'easeIn', ...LEAN },
    { phase: 'active', at: 0, image: W(3), hold: true, tag: 'contact', hitIndex: 0, ease: 'easeOut', ...HIT },
    { phase: 'comboGap', at: 0, image: W(4), hold: true, tag: 'followThrough', hitIndex: 0, ease: 'easeInOut' },
    { phase: 'comboGap', at: 0.17, image: SPIN_IN, hold: true, hitIndex: 0, ease: 'easeIn' },   // 振り抜きから床を押して回り始める
    { phase: 'comboGap', at: 0.35, image: W(5), hold: true, hitIndex: 0, ease: 'easeInOut', ...TURN },
    { phase: 'comboGap', at: 0.7, image: W(6), hold: true, hitIndex: 0, ease: 'easeIn' },
    { phase: 'comboGap', at: 0.999, image: W(6), hold: true, hitIndex: 0, ...READY },
    { phase: 'active', at: 0, image: W(7), hold: true, tag: 'contact', hitIndex: 1, ease: 'easeOut', ...HIT },
    { phase: 'comboGap', at: 0, image: W(8), hold: true, tag: 'followThrough', hitIndex: 1, ease: 'easeInOut' },
    { phase: 'comboGap', at: 0.42, image: SPIN_AGAIN, hold: true, hitIndex: 1, ease: 'easeIn', ...RISE },   // 振り抜きからつま先で伸び上がり、二度目の回転へ
    { phase: 'comboGap', at: 0.7, image: W(9), hold: true, hitIndex: 1, ease: 'easeIn', ...TURN },
    { phase: 'comboGap', at: 0.999, image: W(9), hold: true, hitIndex: 1, ...READY },
    { phase: 'active', at: 0, image: W(10), hold: true, tag: 'contact', hitIndex: 2, ease: 'easeOut', ...HIT },
    { phase: 'recovery', at: 0, image: W(11), hold: true, tag: 'followThrough', ease: 'easeOut' },
    { phase: 'recovery', at: 0.15, image: W(11), hold: true, ease: 'easeOut', ...CARRY },
    { phase: 'recovery', at: 0.17, image: LOWER, hold: true, ease: 'easeIn', ...SETTLE_HI },   // まだ宙に残り、12 で床を踏む
    { phase: 'recovery', at: 0.3, image: W(12), hold: true, tag: 'recover', ease: 'easeOut' },
    { phase: 'recovery', at: 0.55, image: W(12), hold: true, ease: 'easeInOut', ...POSE },
    { phase: 'recovery', at: 0.6, image: SU(1), hold: true, ease: 'easeInOut', ...SETTLE_HI },
    { phase: 'recovery', at: 0.7, image: SU(2), hold: true, ease: 'easeInOut' },
    { phase: 'recovery', at: 0.79, image: STAND, hold: true, ease: 'easeOut', ...SETTLE_HI },   // 膝を伸ばして立ち姿の背丈へ
    { phase: 'recovery', at: 0.88, image: 'idle', hold: true },
  ],
  // 終舞・五連: 斬り下ろし → 横一文字 → 斬り上げ → (上がった剣をそのまま振りかぶりへ) 斬り下ろし → 回って斬り上げで締める。間は 300ms。
  crescendo: [
    { phase: 'telegraph', at: 0, image: SU(2), hold: true, tag: 'anticipation', ease: 'easeOut', ...SETTLE_LO },   // 立ち姿から、戻りの絵を逆にたどって構えへ
    { phase: 'telegraph', at: 0.07, image: WU(1), hold: true, ease: 'easeOut' },
    { phase: 'telegraph', at: 0.14, image: WU(1), hold: true, ease: 'easeOut', ...DIP },
    { phase: 'telegraph', at: 0.17, image: RAISE, hold: true, ease: 'easeIn' },   // 沈みから剣を上げながら伸び上がる途中
    { phase: 'telegraph', at: 0.23, image: WU(2), hold: true, ease: 'easeIn' },
    { phase: 'telegraph', at: 0.38, image: WU(2), hold: true, ease: 'easeOut', ...RISE },
    { phase: 'telegraph', at: 0.4, image: WU(3), hold: true, ease: 'easeOut' },
    { phase: 'telegraph', at: 0.76, image: WU(3), hold: true, ease: 'easeIn', ...COIL },
    { phase: 'telegraph', at: 0.78, image: LAUNCH, hold: true, ease: 'easeIn', ...DRIVE },   // 剣を肩の高さで前へ出しながら踏み込みへ
    { phase: 'telegraph', at: 0.87, image: W(2), hold: true, ease: 'easeIn', ...LEAN },
    { phase: 'active', at: 0, image: W(3), hold: true, tag: 'contact', hitIndex: 0, ease: 'easeOut', ...HIT },
    { phase: 'comboGap', at: 0, image: W(4), hold: true, tag: 'followThrough', hitIndex: 0, ease: 'easeInOut' },
    { phase: 'comboGap', at: 0.17, image: SPIN_IN, hold: true, hitIndex: 0, ease: 'easeIn' },   // 振り抜きから床を押して回り始める
    { phase: 'comboGap', at: 0.35, image: W(5), hold: true, hitIndex: 0, ease: 'easeInOut', ...TURN },
    { phase: 'comboGap', at: 0.7, image: W(6), hold: true, hitIndex: 0, ease: 'easeIn' },
    { phase: 'comboGap', at: 0.999, image: W(6), hold: true, hitIndex: 0, ...READY },
    { phase: 'active', at: 0, image: W(7), hold: true, tag: 'contact', hitIndex: 1, ease: 'easeOut', ...HIT },
    { phase: 'comboGap', at: 0, image: W(8), hold: true, tag: 'followThrough', hitIndex: 1, ease: 'easeInOut' },
    { phase: 'comboGap', at: 0.4, image: SPIN_AGAIN, hold: true, hitIndex: 1, ease: 'easeIn', ...RISE },   // 振り抜きからつま先で伸び上がり、二度目の回転へ
    { phase: 'comboGap', at: 0.65, image: W(9), hold: true, hitIndex: 1, ease: 'easeIn', ...TURN },
    { phase: 'comboGap', at: 0.999, image: W(9), hold: true, hitIndex: 1, ...READY },
    { phase: 'active', at: 0, image: W(10), hold: true, tag: 'contact', hitIndex: 2, ease: 'easeOut', ...HIT },
    { phase: 'comboGap', at: 0, image: W(11), hold: true, tag: 'followThrough', hitIndex: 2, ease: 'easeOut' },
    { phase: 'comboGap', at: 0.18, image: W(11), hold: true, hitIndex: 2, ease: 'easeIn', ...CARRY },
    { phase: 'comboGap', at: 0.2, image: LOWER, hold: true, hitIndex: 2, ease: 'easeIn', ...SETTLE_HI },   // 上がった剣が真上を通って振りかぶりへ
    { phase: 'comboGap', at: 0.35, image: WU(3), hold: true, hitIndex: 2, ease: 'easeIn' },
    { phase: 'comboGap', at: 0.5, image: WU(3), hold: true, hitIndex: 2, ease: 'easeIn', ...COIL },
    { phase: 'comboGap', at: 0.52, image: LAUNCH, hold: true, hitIndex: 2, ease: 'easeIn', ...DRIVE },
    { phase: 'comboGap', at: 0.65, image: W(2), hold: true, hitIndex: 2, ease: 'easeIn', ...LEAN },
    { phase: 'active', at: 0, image: W(3), hold: true, tag: 'contact', hitIndex: 3, ease: 'easeOut', ...HIT },
    { phase: 'comboGap', at: 0, image: W(4), hold: true, tag: 'followThrough', hitIndex: 3, ease: 'easeInOut' },
    { phase: 'comboGap', at: 0.3, image: W(8), hold: true, hitIndex: 3, ease: 'easeInOut' },   // 低い沈み 4 から、中の高さの 8 を通って 9 へ立ち上がる
    { phase: 'comboGap', at: 0.5, image: SPIN_AGAIN, hold: true, hitIndex: 3, ease: 'easeIn', ...RISE },
    { phase: 'comboGap', at: 0.65, image: W(9), hold: true, hitIndex: 3, ease: 'easeIn', ...TURN },
    { phase: 'comboGap', at: 0.999, image: W(9), hold: true, hitIndex: 3, ...READY },
    { phase: 'active', at: 0, image: W(10), hold: true, tag: 'contact', hitIndex: 4, ease: 'easeOut', ...HIT },
    { phase: 'recovery', at: 0, image: W(11), hold: true, tag: 'followThrough', ease: 'easeOut' },
    { phase: 'recovery', at: 0.15, image: W(11), hold: true, ease: 'easeOut', ...CARRY },
    { phase: 'recovery', at: 0.17, image: LOWER, hold: true, ease: 'easeIn', ...SETTLE_HI },   // まだ宙に残り、12 で床を踏む
    { phase: 'recovery', at: 0.3, image: W(12), hold: true, tag: 'recover', ease: 'easeOut' },
    { phase: 'recovery', at: 0.55, image: W(12), hold: true, ease: 'easeInOut', ...POSE },
    { phase: 'recovery', at: 0.6, image: SU(1), hold: true, ease: 'easeInOut', ...SETTLE_HI },
    { phase: 'recovery', at: 0.7, image: SU(2), hold: true, ease: 'easeInOut' },
    { phase: 'recovery', at: 0.79, image: STAND, hold: true, ease: 'easeOut', ...SETTLE_HI },   // 膝を伸ばして立ち姿の背丈へ
    { phase: 'recovery', at: 0.88, image: 'idle', hold: true },
  ],
}
// 当たる瞬間の絵 (3・7・10) の剣先の前端 (画面 px・床から 120px の内)。測り方は上の BLADE_DANCER_MOVE_ART_FRONT_PX と同じ
//   (同じ測り方で旧の4枚が 127/160/157/157 に戻ることを確かめてから測った)。
export const BLADE_DANCER_MOTION_V2_FRONT_PX = { cut: 145, sweep: 152, rise: 133 } as const
const V2_STRIKES: Record<string, (keyof typeof BLADE_DANCER_MOTION_V2_FRONT_PX)[]> = {
  waltzFlurry: ['cut', 'sweep', 'rise'],
  crescendo: ['cut', 'sweep', 'rise', 'cut', 'rise'],
}

/** 動きを1から作った円舞を出す (DEV の ?bladeMotion=v2 だけが呼ぶ・preloadBossSprites より前に呼ぶ)。 */
export function useBladeDancerMotionV2(): void {
  BOSS_ATTACK_TIMELINES[BOSS_ID] = { ...(BOSS_ATTACK_TIMELINES[BOSS_ID] ?? {}), ...BLADE_DANCER_MOTION_V2_TIMELINES }
  applyArtReach(Object.fromEntries(Object.entries(V2_STRIKES)
    .map(([id, ks]) => [id, ks.map(k => BLADE_DANCER_MOTION_V2_FRONT_PX[k])])))
  // 移動は絵が動いている所でする (足滑りを消す)。配布版は当たる瞬間の絵のまま 1900px/s で最大130px滑る (撮影で 250px を測った)。
  //   1発目 = 踏み出す絵 (2) を見せている溜めの最後で寄る。連撃の間 = 回る絵 (5・6・9) の間に、相手が下がった分だけ滑って寄る (ワルツの滑り)。
  //   当たる絵の間は止まる (gapOnly)。溜めで寄り切れなかった分・相手が下がった分は、次の回る絵の間に滑って詰める (上限 V2_GLIDE_PX_S)。
  for (const [id, charge] of Object.entries(V2_STEP_IN)) {
    const a = ATTACKS[id]!
    const hits = a.comboHits ?? 1
    a.preActiveLungeMs = charge.ms
    a.preActiveLungeDistance = a.lungeDistance ?? 130
    a.preActiveLungeStopGapPx = a.approachDistancePx! - V2_STEP_IN_SHORT_PX
    a.preActiveLungeMinPx = V2_STEP_IN_MIN_PX
    // 加速しきった所で当たる絵へ替わって止まると、体が前へ飛んで見えた (最後の1コマで約1440px/s → 0)。
    //   滑らかに加速して減速し、当たる瞬間に止まる = 踏み出した前足が着いて剣が届く。
    a.preActiveLungeEase = 'smooth'
    a.comboLunge = { followHits: hits, followSpeed: V2_GLIDE_PX_S, advanceSpeed: 0, plantFromHit: hits, gapOnly: true, glideRampMs: V2_GLIDE_RAMP_MS, glideBrakePerSec: V2_GLIDE_BRAKE_PER_S }
  }
}
// メリハリ版 (2026-10-19 作者の答え・DEV ?bladeMotion=v3)。v2 と同じ絵から、振る所を速く・止める所をはっきりにした版。
//   作者「最初の掲げる前から掲げ切るところは結構いい感じ」「変に見えるポーズが一枚挟まって…ジャンガジャンガ」「両刃剣になってる絵が挟まってる」
//   「動きがモチモチ」「指揮者の指揮棒を振っているよう」「弾かれた絵を挟むのはおかしい・しっかりしたボスは弾かれてもひるまない」
//   「最初の予備動作を見た後のリズムは目をつぶっても押せる (右手を掲げたらタンタンタン・顔も上がっていたらタンタタタン)」。
//   決め方:
//     ・溜めの入り (沈み → 剣を上げる → 掲げ切る) は v2 のまま。掲げ切った見得を長く止め、そこから踏み出す 2 → 当たる 3 へ一気に出る (間の絵 c2 は抜く)。
//     ・技ごとに掲げ切る見得を変える = 見得でその後のリズムが分かる。円舞 (3拍) = w3 (右手で剣を掲げる)／終舞 (5拍) = c1 (つま先で伸び上がり、剣を真上へ)。
//     ・連撃の間は、振り抜いた絵で止める (見得) → 回る絵を短く → 当たる直前の絵 → 当たる。間の絵を均等に置かない (v2 は 4〜5枚を均等に置き、モチモチして見えた)。
//     ・両刃剣に見えた c3・c5 (片方の剣が体の前を横切り、もう片方の剣とひと続きに見える) は使わない。
//     ・伸び縮み (scale) は使わない。見得の間だけ、傾きと少しの引きで力を溜める。
//     ・弾かれても絵を替えず・重ねず、6px だけ押し戻す (ひるまない・src/sprites.ts setBossNoFlinch)。
export const TENSE = { offset: { x: -6 }, rot: -3 }   // 見得の間に、剣を引いて力を溜める (伸び縮みはしない)
const PEAK_CRES = LOWER                          // 終舞の見得 = つま先で伸び上がり剣を真上へ (c1)
export const BLADE_DANCER_MOTION_V3_TIMELINES: Record<string, BossAttackTimeline> = {
  waltzFlurry: [
    { phase: 'telegraph', at: 0, image: SU(2), hold: true, tag: 'anticipation' },
    { phase: 'telegraph', at: 0.07, image: WU(1), hold: true },
    { phase: 'telegraph', at: 0.17, image: RAISE, hold: true },
    { phase: 'telegraph', at: 0.25, image: WU(2), hold: true },
    { phase: 'telegraph', at: 0.38, image: WU(3), hold: true, ease: 'easeIn' },   // 掲げ切って止める (見得)
    { phase: 'telegraph', at: 0.85, image: WU(3), hold: true, ...TENSE },
    { phase: 'telegraph', at: 0.86, image: W(2), hold: true },                     // 一気に踏み出す
    { phase: 'active', at: 0, image: W(3), hold: true, tag: 'contact', hitIndex: 0 },
    { phase: 'comboGap', at: 0, image: W(4), hold: true, tag: 'followThrough', hitIndex: 0 },   // 振り抜いて止める
    { phase: 'comboGap', at: 0.55, image: W(5), hold: true, hitIndex: 0 },                       // 速く回る
    { phase: 'comboGap', at: 0.7, image: W(6), hold: true, hitIndex: 0 },
    { phase: 'active', at: 0, image: W(7), hold: true, tag: 'contact', hitIndex: 1 },
    { phase: 'comboGap', at: 0, image: W(8), hold: true, tag: 'followThrough', hitIndex: 1 },
    { phase: 'comboGap', at: 0.7, image: W(9), hold: true, hitIndex: 1 },
    { phase: 'active', at: 0, image: W(10), hold: true, tag: 'contact', hitIndex: 2 },
    { phase: 'recovery', at: 0, image: W(11), hold: true, tag: 'followThrough' },
    { phase: 'recovery', at: 0.22, image: W(12), hold: true, tag: 'recover', ease: 'easeOut' },
    { phase: 'recovery', at: 0.55, image: W(12), hold: true, rot: -2 },
    { phase: 'recovery', at: 0.6, image: SU(1), hold: true },
    { phase: 'recovery', at: 0.7, image: SU(2), hold: true },
    { phase: 'recovery', at: 0.79, image: STAND, hold: true },
    { phase: 'recovery', at: 0.88, image: 'idle', hold: true },
  ],
  crescendo: [
    { phase: 'telegraph', at: 0, image: SU(2), hold: true, tag: 'anticipation' },
    { phase: 'telegraph', at: 0.07, image: WU(1), hold: true },
    { phase: 'telegraph', at: 0.17, image: RAISE, hold: true },
    { phase: 'telegraph', at: 0.25, image: WU(2), hold: true },
    { phase: 'telegraph', at: 0.38, image: PEAK_CRES, hold: true, ease: 'easeIn' },   // 伸び上がって剣を真上へ (見得)
    { phase: 'telegraph', at: 0.87, image: PEAK_CRES, hold: true, ...TENSE },
    { phase: 'telegraph', at: 0.88, image: W(2), hold: true },
    { phase: 'active', at: 0, image: W(3), hold: true, tag: 'contact', hitIndex: 0 },
    { phase: 'comboGap', at: 0, image: W(4), hold: true, tag: 'followThrough', hitIndex: 0 },
    { phase: 'comboGap', at: 0.55, image: W(5), hold: true, hitIndex: 0 },
    { phase: 'comboGap', at: 0.72, image: W(6), hold: true, hitIndex: 0 },
    { phase: 'active', at: 0, image: W(7), hold: true, tag: 'contact', hitIndex: 1 },
    { phase: 'comboGap', at: 0, image: W(8), hold: true, tag: 'followThrough', hitIndex: 1 },
    { phase: 'comboGap', at: 0.72, image: W(9), hold: true, hitIndex: 1 },
    { phase: 'active', at: 0, image: W(10), hold: true, tag: 'contact', hitIndex: 2 },
    { phase: 'comboGap', at: 0, image: W(11), hold: true, tag: 'followThrough', hitIndex: 2 },
    { phase: 'comboGap', at: 0.4, image: WU(3), hold: true, hitIndex: 2 },          // 上がった剣をそのまま掲げ直す
    { phase: 'comboGap', at: 0.72, image: W(2), hold: true, hitIndex: 2 },
    { phase: 'active', at: 0, image: W(3), hold: true, tag: 'contact', hitIndex: 3 },
    { phase: 'comboGap', at: 0, image: W(4), hold: true, tag: 'followThrough', hitIndex: 3 },
    { phase: 'comboGap', at: 0.7, image: W(9), hold: true, hitIndex: 3 },
    { phase: 'active', at: 0, image: W(10), hold: true, tag: 'contact', hitIndex: 4 },
    { phase: 'recovery', at: 0, image: W(11), hold: true, tag: 'followThrough' },
    { phase: 'recovery', at: 0.22, image: W(12), hold: true, tag: 'recover', ease: 'easeOut' },
    { phase: 'recovery', at: 0.55, image: W(12), hold: true, rot: -2 },
    { phase: 'recovery', at: 0.6, image: SU(1), hold: true },
    { phase: 'recovery', at: 0.7, image: SU(2), hold: true },
    { phase: 'recovery', at: 0.79, image: STAND, hold: true },
    { phase: 'recovery', at: 0.88, image: 'idle', hold: true },
  ],
}
/** メリハリ版を出す (DEV の ?bladeMotion=v3 だけが呼ぶ)。当たる絵・寄り方は v2 と同じ。弾かれてもひるまない。 */
export function useBladeDancerMotionV3(): void {
  useBladeDancerMotionV2()
  BOSS_ATTACK_TIMELINES[BOSS_ID] = { ...BOSS_ATTACK_TIMELINES[BOSS_ID], ...BLADE_DANCER_MOTION_V3_TIMELINES }
  setBossNoFlinch(BOSS_ID, true)
}
// 円舞の3回目 (2026-10-20 作者の答え・DEV ?bladeMotion=v4)。v3 の上に、次の4つを直した版。
//   作者「途中で一瞬関係のない絵が挟まっている」「剣の位置が飛びすぎてる」(E-1・E-4) → 連撃の間の回る絵 5 を外し、4 → 6 にする
//     (分析 = docs/research/2026-10-20-blade-dancer-waltz-odd-frame.md)。
//   作者「ラクサシアの…剣先をプレイヤーの方に向けて…ホームラン宣言みたいな感じでがっつり前に構える」「気持ち長めに見せる」(E-2)
//     → 終舞の見得を、前の剣を伸ばし切って剣先を相手の顔へ向ける構え (a2・紙 cres_aim.png) にし、溜めを 900 → 1050ms にして長く見せる。
//       3連の見得 (w3・剣を上に掲げ剣先が後ろ) とは形がはっきり違う。踏み出す 2 からあとの時刻は v3 と同じ。
//   作者「構える時とか、最後の終わりの構えの時の絵の切り替え…ちょっとカクカク」→ 溜めの入りと戻りの絵の替わり目を、
//     替わる前の半分 (最大 60ms) だけ溶かして替える。当たる絵・連撃の間・踏み出す 2 はぱっと替えたまま (弾く拍を濁らせない)。
//     戻りは絵の間隔をそろえた (v3 は最後の 200ms に4枚が詰まっていた)。
//   作者「切るときのエフェクトがない」→ 当たる絵 3・7・10 に、剣先の通った跡を短く出す (BLADE_DANCER_V4_SMEAR・src/render.ts の接触の跡)。
//   弾かれてもひるまないのは v3 のまま (E-3「この方がいい」)。
// 当たる絵ごとの剣先の通り道 (仮・見本の絵なしでコードから作った。検収パックで作者に聞く)。
//   剣先は実機で測った (10-20・__freezeBossComboHit → __bossBodyPlacement・足元からの 前/上 px):
//   3 = 前145/上43 (前の剣が前下へ)・7 = 前152/上92 (肩の高さで水平)・10 = 前133/上34。当たる絵には offset/rot が無いのでそのまま使える。
//   振りの向きは 下ろす (3) → 振り上げる (7) → 下ろす (10) と交互にし、どの弧も終わりが剣先に乗る。
//   色は冷たい銀の芯と藤色のにじみ (白い線にしない・赤は使わない)。太さは騎士の薙ぎより細い (片手の細い刃)。
export const BLADE_SMEAR_BASE = {
  kind: 'arc' as const, widthPx: 6, maxAlpha: 0.95, lifeMs: 200, sparks: true,
  afterContact: { fromP: 0.45, revealMs: 33, holdMs: 45, fadeMs: 150 },
  coreColor: '#e6dcff', glowColor: '#a585e0',
}
export const BLADE_DANCER_V4_SMEAR: Record<string, ContactSmearArc> = {
  // 1発目: 頭の前上 (前80/上194) から前下の剣先へ斬り下ろす。
  m1020_waltz_3: { ...BLADE_SMEAR_BASE, anchorFwdPx: 0, anchorUpPx: 70, radiusPx: 147.5, angleFromRad: 1.0, angleToRad: -0.184 },
  // 2発目: 前の足元 (前93/上6) から肩の高さの剣先へ振り上げる。
  m1020_waltz_7: { ...BLADE_SMEAR_BASE, anchorFwdPx: 20, anchorUpPx: 120, radiusPx: 134.9, angleFromRad: -1.0, angleToRad: -0.209 },
  // 3発目 (締め): 1発目より大きく、頭の上 (前51/上217) から斬り下ろす。
  m1020_waltz_10: { ...BLADE_SMEAR_BASE, anchorFwdPx: -10, anchorUpPx: 80, radiusPx: 150.2, angleFromRad: 1.15, angleToRad: -0.311, widthPx: 7 },
}
const AIM_IN = 'm1020_waltz_a1'   // 剣を上げた姿から、前の剣を相手へ向けて下ろしてくる途中
const AIM = 'm1020_waltz_a2'      // 前の剣を伸ばし切って剣先を相手の顔の高さへ向ける (ホームラン宣言)・後ろの剣は後ろ下へ
const V4_CRESCENDO_CHARGE_MS = 1050
const DISSOLVE_SHARE = 0.5
const DISSOLVE_MAX_MS = 60
export type SoftKey = BossAttackTimeline[number] & { soft?: true }
/** soft の付いたキーから次のキーへの替わり目を、替わる前の DISSOLVE_SHARE (最大 DISSOLVE_MAX_MS) だけ溶かすキーを差し込む。 */
export function withDissolves(keys: SoftKey[], phaseMs: Partial<Record<BossAttackTimeline[number]['phase'], number>>): BossAttackTimeline {
  const out: BossAttackTimeline = []
  keys.forEach((k, i) => {
    const { soft, ...key } = k
    out.push(key)
    const n = keys[i + 1]
    if (!soft || !n || n.phase !== k.phase || n.image === k.image) return
    const spanMs = (n.at - k.at) * (phaseMs[k.phase] ?? 0)
    const dMs = Math.min(spanMs * DISSOLVE_SHARE, DISSOLVE_MAX_MS)
    if (dMs <= 0) return
    const at = n.at - dMs / (phaseMs[k.phase] ?? 1)
    const u = (at - k.at) / (n.at - k.at)
    const t = k.ease === 'easeIn' ? u * u : k.ease === 'easeOut' ? 1 - (1 - u) * (1 - u) : u
    const lerp = (a = 0, b = 0) => a + (b - a) * t
    const ox = lerp(k.offset?.x, n.offset?.x), oy = lerp(k.offset?.y, n.offset?.y), rot = lerp(k.rot, n.rot)
    out.push({ phase: k.phase, at, image: k.image, hold: false,
      ...(ox || oy ? { offset: { x: ox, y: oy } } : {}), ...(rot ? { rot } : {}),
      ...(k.hitIndex !== undefined ? { hitIndex: k.hitIndex } : {}) })
  })
  return out
}
const V4_RECOVERY: SoftKey[] = [
  { phase: 'recovery', at: 0, image: W(11), hold: true, tag: 'followThrough', soft: true },
  { phase: 'recovery', at: 0.22, image: W(12), hold: true, tag: 'recover', ease: 'easeOut' },
  { phase: 'recovery', at: 0.42, image: W(12), hold: true, rot: -2, soft: true },
  { phase: 'recovery', at: 0.5, image: SU(1), hold: true, soft: true },
  { phase: 'recovery', at: 0.62, image: SU(2), hold: true, soft: true },
  { phase: 'recovery', at: 0.74, image: STAND, hold: true, soft: true },
  { phase: 'recovery', at: 0.86, image: 'idle', hold: true },
]
const v3Keys = (id: string, from: (k: BossAttackTimeline[number]) => boolean) => BLADE_DANCER_MOTION_V3_TIMELINES[id]!
  .filter(k => from(k) && k.image !== W(5))
const isGapOrHit = (k: BossAttackTimeline[number]) => k.phase === 'active' || k.phase === 'comboGap'
export const BLADE_DANCER_MOTION_V4_TIMELINES: Record<string, BossAttackTimeline> = {
  waltzFlurry: withDissolves([
    { phase: 'telegraph', at: 0, image: SU(2), hold: true, tag: 'anticipation', soft: true },
    { phase: 'telegraph', at: 0.07, image: WU(1), hold: true, soft: true },
    { phase: 'telegraph', at: 0.17, image: RAISE, hold: true, soft: true },
    { phase: 'telegraph', at: 0.25, image: WU(2), hold: true, soft: true },
    { phase: 'telegraph', at: 0.38, image: WU(3), hold: true, ease: 'easeIn' },   // 掲げ切って止める (見得)
    { phase: 'telegraph', at: 0.85, image: WU(3), hold: true, ...TENSE },
    { phase: 'telegraph', at: 0.86, image: W(2), hold: true },                     // 一気に踏み出す
    ...v3Keys('waltzFlurry', isGapOrHit),
    ...V4_RECOVERY,
  ], { telegraph: 800, recovery: 720 }),
  crescendo: withDissolves([
    { phase: 'telegraph', at: 0, image: SU(2), hold: true, tag: 'anticipation', soft: true },
    { phase: 'telegraph', at: 0.06, image: WU(1), hold: true, soft: true },
    { phase: 'telegraph', at: 0.145, image: RAISE, hold: true, soft: true },
    { phase: 'telegraph', at: 0.215, image: WU(2), hold: true, soft: true },
    { phase: 'telegraph', at: 0.3, image: AIM_IN, hold: true, soft: true },         // 剣先を相手へ向けて下ろしてくる
    { phase: 'telegraph', at: 0.37, image: AIM, hold: true, ease: 'easeIn' },      // 剣先を相手の顔へ向けて構える (見得・約550ms)
    { phase: 'telegraph', at: 0.885, image: AIM, hold: true, ...TENSE },
    { phase: 'telegraph', at: 0.895, image: W(2), hold: true },                    // 踏み出す 2 の出る時刻と長さは v3 と同じ (110ms)
    ...v3Keys('crescendo', isGapOrHit),
    ...V4_RECOVERY,
  ], { telegraph: V4_CRESCENDO_CHARGE_MS, recovery: 1050 }),
}
/** 3回目の版を出す (DEV の ?bladeMotion=v4 だけが呼ぶ)。v3 の上に、終舞の溜めの長さ・替わり目の溶かし・斬りの跡を足す。 */
export function useBladeDancerMotionV4(): void {
  useBladeDancerMotionV3()
  BOSS_ATTACK_TIMELINES[BOSS_ID] = { ...BOSS_ATTACK_TIMELINES[BOSS_ID], ...BLADE_DANCER_MOTION_V4_TIMELINES }
  ATTACKS.crescendo!.chargeMs = V4_CRESCENDO_CHARGE_MS
  setBossTimelineCrossfade(BOSS_ID, true)
  Object.assign(SMEAR_ARTS, BLADE_DANCER_V4_SMEAR)
  CONTACT_SMEAR_BOSSES.add(BOSS_ID)
}
// 4回目 (2026-10-20 作者の答え F-2・DEV ?bladeMotion=v5)。記録 = docs/research/2026-10-20-blade-dancer-invite-r4.md
//   作者「相手を見据えつつ、あなたの元に今から行きますよって感じ…ただ、戦いの意味での行くぞっていう感じが強い…もうちょっと踊りっぽく」
//   → 終舞の構えを、剣先を向ける構え (a1・a2) から、舞踏会の誘いに替える。お辞儀 (b1) → 立ち上がる (b2) → 手を差し伸べる (b3)。
//     絵は Codex の紙 cres_invite.png (1段3コマ)。お辞儀でも頭は相手へ向いたまま。誘いは前の手のひらを相手へ差し伸べ、剣は指さない。
//   誘いのあいだ (約510ms) は相手へ少しずつ寄って前へ乗る (戦いの「引いて溜める」TENSE は使わない)。踏み出す 2 からあとと円舞 (3連) は v4 と同じ。
const BOW = 'm1020_waltz_b1'      // お辞儀: 後ろの脚を引いて両膝を曲げ、両腕を低く開いて剣を外へ寝かせる・頭は相手へ
const BOW_RISE = 'm1020_waltz_b2' // お辞儀から立ち上がる途中: 前の腕が相手へ伸び、後ろの腕が上がり始める
const INVITE = 'm1020_waltz_b3'   // 誘い: つま先で伸び上がり、前の手を相手へ差し伸べ、後ろの腕は頭の上で弧
const BOW_SINK = { scale: { x: 1.01, y: 0.98 } }       // お辞儀の底へもう一段沈む
const DRAW_NEAR = { offset: { x: 8 }, rot: 2 }         // 誘ったまま、相手へ少し寄って前へ乗る
export const BLADE_DANCER_MOTION_V5_TIMELINES: Record<string, BossAttackTimeline> = {
  crescendo: withDissolves([
    { phase: 'telegraph', at: 0, image: SU(2), hold: true, tag: 'anticipation', soft: true },
    { phase: 'telegraph', at: 0.06, image: BOW, hold: true, ease: 'easeOut' },          // 相手を見たまま、深くお辞儀する
    { phase: 'telegraph', at: 0.22, image: BOW, hold: true, ...BOW_SINK, soft: true },
    { phase: 'telegraph', at: 0.3, image: BOW_RISE, hold: true, soft: true },          // 立ち上がりながら手を差し出していく
    { phase: 'telegraph', at: 0.4, image: INVITE, hold: true, ease: 'easeInOut' },     // 手を差し伸べて誘う (見得・約510ms)
    { phase: 'telegraph', at: 0.885, image: INVITE, hold: true, ...DRAW_NEAR },
    { phase: 'telegraph', at: 0.895, image: W(2), hold: true },                        // 誘った手の方へ一気に踏み出す (v4 と同じ時刻)
    ...v3Keys('crescendo', isGapOrHit),
    ...V4_RECOVERY,
  ], { telegraph: V4_CRESCENDO_CHARGE_MS, recovery: 1050 }),
}
/** 4回目の版を出す (DEV の ?bladeMotion=v5 だけが呼ぶ)。v4 の上で、終舞の構えだけを誘いの所作に替える。 */
export function useBladeDancerMotionV5(): void {
  useBladeDancerMotionV4()
  BOSS_ATTACK_TIMELINES[BOSS_ID] = { ...BOSS_ATTACK_TIMELINES[BOSS_ID], ...BLADE_DANCER_MOTION_V5_TIMELINES }
}
// 5回目 (2026-10-20 作者の答え G・10-21 から配布版の既定)。記録 = docs/research/2026-10-20-blade-dancer-strike-table-v6.md
//   作者「連撃の絵が全部似通って…ずっと突き攻撃をしているよう」「いまだに剣先が両方付いている」「不自然に体が伸びきっている状態なのに、ずっと上下に剣を振っている」
//   → 3連・5連の8発を1発ずつ言葉の表に起こし、Codex に1発4コマずつ描き直させた (紙 7枚・28コマ・m1020_v6_<紙><コマ>)。
//     どの発も当たる絵は別の絵。2本の剣は体をはさんで一直線に並べない (両刃に見える形を外す)。発ごとに膝と腰の高さを変える。
//   お辞儀は始めから外し、5連の締めの礼 (g4) へ移す (作者「お辞儀が何の話かわからない」= 誘いの前の 0.2 秒では見えなかった)。
//   当たる瞬間の火花 = 作者の答え「B 刃の花びら・2番目 D 砕ける鏡」→ 花びらと鏡の破片を刃先から散らす (render.ts ContactSmearPetals)。
//     散る向きを発ごとに変える (3連 = 斜め下へこぼれる → 横へ流れる → 上へ舞い上がる)。
//   当たる時刻・溜め・間・戻りの長さは v5 と同じ。寄り方・ひるまない・替わり目の溶かしも v5 のまま。
const V = (k: string) => `m1020_v6_${k}`
// 当たる絵の剣先の前端 (画面 px・床から 120px の内)。測り方は上と同じ (原画の不透明画素を置き方の縮尺で画面へ直した値)。
export const BLADE_DANCER_MOTION_V6_FRONT_PX = {
  a3: 133,   // 3連1・袈裟に斬り下ろす (剣先 前132/上16)
  b3: 128,   // 3連2・回って腰の高さを横に薙ぐ (前126/上114)
  c3: 110,   // 3連3・伸び上がって斬り上げる (前69/上236・主人公の背丈の内は前110まで)
  d2: 115,   // 5連1・逆袈裟に斬り上げる (剣先 前100/上222)
  d4: 116,   // 5連2・袈裟に斬り下ろす (剣先 前116/上34)
  e3: 148,   // 5連3・沈んで膝の高さを横に薙ぐ (前148/上84)
  f2: 103,   // 5連4・はさみのように2本を外へ開く (下の剣 前101/上53)
  g1: 116,   // 5連5・2本をそろえて頭上から振り下ろす (前116/上40)
} as const
const V6_STRIKES: Record<string, (keyof typeof BLADE_DANCER_MOTION_V6_FRONT_PX)[]> = {
  waltzFlurry: ['a3', 'b3', 'c3'],
  crescendo: ['d2', 'd4', 'e3', 'f2', 'g1'],
}
// 剣先の通り道。どの弧も終わりが当たる絵の剣先に乗る (剣先 = anchor + 半径 × (cos 終わりの角, sin 終わりの角))。
export const BLADE_DANCER_V6_SMEAR: Record<string, ContactSmearArc> = {
  [V('a3')]: { ...BLADE_SMEAR_BASE, anchorFwdPx: 10, anchorUpPx: 110, radiusPx: 154, angleFromRad: 1.0, angleToRad: -0.656,
    petals: { spread: 'down', count: 10, seed: 1, sizePx: 22 } },
  // 後ろの腰の高さから一番低い所を通って前の剣先へ抜ける、平らな横一閃 (大きな半径の弧の底だけを使う)。
  [V('b3')]: { ...BLADE_SMEAR_BASE, anchorFwdPx: 20, anchorUpPx: 330, radiusPx: 240.6, angleFromRad: -1.9, angleToRad: -1.114,
    petals: { spread: 'side', count: 11, seed: 2, sizePx: 24 } },
  [V('c3')]: { ...BLADE_SMEAR_BASE, anchorFwdPx: 10, anchorUpPx: 150, radiusPx: 104, angleFromRad: -1.2, angleToRad: 0.97, widthPx: 7,
    petals: { spread: 'up', count: 12, seed: 3, sizePx: 27 } },
  [V('d2')]: { ...BLADE_SMEAR_BASE, anchorFwdPx: 10, anchorUpPx: 110, radiusPx: 143.3, angleFromRad: -1.0, angleToRad: 0.895,
    petals: { spread: 'up', count: 10, seed: 4, sizePx: 22 } },
  [V('d4')]: { ...BLADE_SMEAR_BASE, anchorFwdPx: 10, anchorUpPx: 120, radiusPx: 136.6, angleFromRad: 1.1, angleToRad: -0.682,
    petals: { spread: 'down', count: 10, seed: 5, sizePx: 22 } },
  [V('e3')]: { ...BLADE_SMEAR_BASE, anchorFwdPx: 20, anchorUpPx: 290, radiusPx: 242.5, angleFromRad: -1.85, angleToRad: -1.016,
    petals: { spread: 'side', count: 11, seed: 6, sizePx: 24 } },
  [V('f2')]: { ...BLADE_SMEAR_BASE, anchorFwdPx: 20, anchorUpPx: 125, radiusPx: 108, angleFromRad: 0, angleToRad: -0.727,
    petals: { spread: 'side', count: 10, seed: 7, sizePx: 22 } },
  [V('g1')]: { ...BLADE_SMEAR_BASE, anchorFwdPx: 0, anchorUpPx: 130, radiusPx: 146.8, angleFromRad: 1.3, angleToRad: -0.659, widthPx: 9,
    petals: { spread: 'down', count: 13, seed: 8, sizePx: 29 } },
}
// 斬りの跡の描き方。10-21 作者「火花なのがおかしい。今は星や火花の攻撃に見える…切っ先の軌道みたいなエフェクトを作るべき」で
//   火花と花びらをやめ、剣先の通り道の帯にした。afterimage = 帯の残像 (既定) / glass = 硝子の弧 / petals = 10-20 の火花と花びら。
export type BladeTrailStyle = 'afterimage' | 'glass' | 'petals'
// 帯は火花より長く残す (振りの形が読めるように)。弧の形 (anchor・半径・角度) は火花の版と同じ。
const V6_TRAIL_AFTER_CONTACT = { fromP: 0.45, revealMs: 40, holdMs: 70, fadeMs: 200 }
export function bladeDancerV6Smear(style: BladeTrailStyle, arcs: Record<string, ContactSmearArc> = BLADE_DANCER_V6_SMEAR): Record<string, ContactSmearArc> {
  if (style === 'petals') return arcs
  return Object.fromEntries(Object.entries(arcs).map(([k, d]) => {
    const { sparks: _s, petals: _p, ...arc } = d
    return [k, { ...arc, afterContact: V6_TRAIL_AFTER_CONTACT, trail: style, trailWidthPx: Math.round(Math.max(14, d.radiusPx * 0.17)) }]
  }))
}
// 戻りの絵 (s1・s2・c6) は v4 と同じ。v6 の絵はどれも v2 の絵と同じ背丈・足元でそろえて置いた (sprites.ts BLADE_DANCER_ANCHOR_X)。
// 5連の戻り (ms)。10-21 作者「礼が短い」で 1050 から延ばした。
const V6_CRESCENDO_RECOVERY_MS = 1450
export const BLADE_DANCER_MOTION_V6_TIMELINES: Record<string, BossAttackTimeline> = {
  // 円舞連斬 (3連・3拍子): 袈裟に斬り下ろす → 回って腰の高さを横に薙ぐ → かがんでから伸び上がって斬り上げ、V字の見得で止まる。
  waltzFlurry: withDissolves([
    { phase: 'telegraph', at: 0, image: SU(2), hold: true, tag: 'anticipation', soft: true },
    { phase: 'telegraph', at: 0.07, image: WU(1), hold: true, soft: true },
    { phase: 'telegraph', at: 0.17, image: RAISE, hold: true, soft: true },
    { phase: 'telegraph', at: 0.25, image: WU(2), hold: true, soft: true },
    { phase: 'telegraph', at: 0.38, image: V('a1'), hold: true, ease: 'easeIn' },   // 沈んで前の剣を頭上高く掲げる (見得)
    { phase: 'telegraph', at: 0.85, image: V('a1'), hold: true, ...TENSE },
    { phase: 'telegraph', at: 0.86, image: V('a2'), hold: true },                   // 一気に踏み込んで振り出す
    { phase: 'active', at: 0, image: V('a3'), hold: true, tag: 'contact', hitIndex: 0 },
    { phase: 'comboGap', at: 0, image: V('a4'), hold: true, tag: 'followThrough', hitIndex: 0 },   // 前膝の外まで振り下ろして止める
    { phase: 'comboGap', at: 0.5, image: V('b1'), hold: true, hitIndex: 0 },                        // 背中を見せて回る
    { phase: 'comboGap', at: 0.72, image: V('b2'), hold: true, hitIndex: 0 },                       // 回り終わり・剣を引き付けて溜める
    { phase: 'active', at: 0, image: V('b3'), hold: true, tag: 'contact', hitIndex: 1 },
    { phase: 'comboGap', at: 0, image: V('b4'), hold: true, tag: 'followThrough', hitIndex: 1 },   // 体の反対側まで薙ぎ抜く
    { phase: 'comboGap', at: 0.5, image: V('c1'), hold: true, hitIndex: 1 },                        // 低くかがんで2本を後ろへ引く
    { phase: 'comboGap', at: 0.75, image: V('c2'), hold: true, hitIndex: 1 },                       // 伸び上がり始める
    { phase: 'active', at: 0, image: V('c3'), hold: true, tag: 'contact', hitIndex: 2 },
    { phase: 'recovery', at: 0, image: V('c4'), hold: true, tag: 'followThrough' },                 // つま先で2本を頭上へ V 字に掲げる (見得)
    { phase: 'recovery', at: 0.3, image: V('c4'), hold: true, tag: 'recover', rot: -2, soft: true },
    { phase: 'recovery', at: 0.42, image: SU(1), hold: true, soft: true },
    { phase: 'recovery', at: 0.56, image: SU(2), hold: true, soft: true },
    { phase: 'recovery', at: 0.7, image: STAND, hold: true, soft: true },
    { phase: 'recovery', at: 0.84, image: 'idle', hold: true },
  ], { telegraph: 800, recovery: 720 }),
  // 終舞・五連: 誘い → 逆袈裟 → 返して袈裟 → 回って沈み膝の高さを横に薙ぐ → はさみ → 2本をそろえて振り下ろし → 立ち上がって礼。
  crescendo: withDissolves([
    { phase: 'telegraph', at: 0, image: SU(2), hold: true, tag: 'anticipation', soft: true },
    { phase: 'telegraph', at: 0.08, image: BOW_RISE, hold: true, ease: 'easeOut', soft: true },   // 立ち上がりながら手を差し出していく
    { phase: 'telegraph', at: 0.3, image: INVITE, hold: true, ease: 'easeInOut' },               // 手を差し伸べて誘う (見得・約620ms)
    { phase: 'telegraph', at: 0.885, image: INVITE, hold: true, ...DRAW_NEAR },
    { phase: 'telegraph', at: 0.895, image: V('d1'), hold: true },                                // 誘った手の方へ踏み出す (v5 と同じ時刻)
    { phase: 'active', at: 0, image: V('d2'), hold: true, tag: 'contact', hitIndex: 0 },
    { phase: 'comboGap', at: 0, image: V('d3'), hold: true, tag: 'followThrough', hitIndex: 0 },   // 剣が頭上で返る = 次の袈裟の構え
    { phase: 'active', at: 0, image: V('d4'), hold: true, tag: 'contact', hitIndex: 1 },
    { phase: 'comboGap', at: 0, image: V('e1'), hold: true, tag: 'followThrough', hitIndex: 1 },   // 振り抜いて下まで
    { phase: 'comboGap', at: 0.55, image: V('e2'), hold: true, hitIndex: 1 },                       // 回り始める
    { phase: 'active', at: 0, image: V('e3'), hold: true, tag: 'contact', hitIndex: 2 },
    { phase: 'comboGap', at: 0, image: V('e4'), hold: true, tag: 'followThrough', hitIndex: 2 },   // 反対側へ薙ぎ抜く
    { phase: 'comboGap', at: 0.6, image: V('f1'), hold: true, hitIndex: 2 },                        // 胸の前で2本を交差させる
    { phase: 'active', at: 0, image: V('f2'), hold: true, tag: 'contact', hitIndex: 3 },
    { phase: 'comboGap', at: 0, image: V('f3'), hold: true, tag: 'followThrough', hitIndex: 3 },   // 2本とも後ろへ流れる
    { phase: 'comboGap', at: 0.6, image: V('f4'), hold: true, hitIndex: 3 },                        // 締めの構え
    { phase: 'active', at: 0, image: V('g1'), hold: true, tag: 'contact', hitIndex: 4 },
    { phase: 'recovery', at: 0, image: V('g2'), hold: true, tag: 'followThrough', soft: true },     // 剣先が床近くまで下りる
    { phase: 'recovery', at: 0.18, image: V('g3'), hold: true, tag: 'recover', soft: true },       // 立ち上がる途中
    { phase: 'recovery', at: 0.28, image: V('g4'), hold: true, ease: 'easeOut', soft: true },      // 締めの礼 (約780ms・10-21 作者「礼が短い」)
    { phase: 'recovery', at: 0.82, image: STAND, hold: true, soft: true },
    { phase: 'recovery', at: 0.91, image: 'idle', hold: true },
  ], { telegraph: V4_CRESCENDO_CHARGE_MS, recovery: V6_CRESCENDO_RECOVERY_MS }),
}
// 寄りの止まりの余り (approachDistancePx = 一番短い前端 + 15 から引く)。v2〜v5 は 28 (剣先が体の 28px 内まで入っていた)。
const V6_STOP_BUFFER_PX = 8
/** 5回目の版を出す (10-21 から配布版の既定・main.ts が ?bladeMotion=v2〜v5・old の時以外に呼ぶ)。v5 の上で、3連と5連の絵・当たりの前端・火花を替える。 */
export function useBladeDancerMotionV6(trail: BladeTrailStyle = 'afterimage'): void {
  useBladeDancerMotionV5()
  BOSS_ATTACK_TIMELINES[BOSS_ID] = { ...BOSS_ATTACK_TIMELINES[BOSS_ID], ...BLADE_DANCER_MOTION_V6_TIMELINES }
  applyBladeDancerV6Reach(1)
  // 締めの礼を約 780ms 見せる分、5連の戻りを 1050 → 1450ms に延ばす (絵の並びの割合は 1450ms で決めてある)。
  ATTACKS.crescendo!.recoveryMs = V6_CRESCENDO_RECOVERY_MS
  Object.assign(SMEAR_ARTS, bladeDancerV6Smear(trail))
  if (trail === 'petals') preloadBladePetals()
}
// 腰の結び目は体の芯から前へ 119 原画px = 画面 15px (186 × 119 ÷ 1437)。絵の倍率を替える時はここを中心に縮む (sprites.ts setBladeDancerBodyFix)。
export const BLADE_DANCER_KNOT_FWD_PX = 15
/** v6 の円舞・終舞の当たりを、当たる絵の剣先の前端で決める。scale = 絵の倍率の比 (10-23 体の大きさをそろえた時に絵と一緒に縮める・src/blade_body_size.ts)。 */
export function applyBladeDancerV6Reach(scale: number): void {
  const fit = (px: number) => Math.round(BLADE_DANCER_KNOT_FWD_PX + (px - BLADE_DANCER_KNOT_FWD_PX) * scale)
  applyArtReach(Object.fromEntries(Object.entries(V6_STRIKES)
    .map(([id, ks]) => [id, ks.map(k => fit(BLADE_DANCER_MOTION_V6_FRONT_PX[k]))])))
  // 溜めの最後の寄りの止まりは、新しい前端で決め直す (v2 で古い前端から決めていたため)。
  for (const id of Object.keys(V6_STRIKES)) {
    const a = ATTACKS[id]!
    // 10-21 作者「近づきすぎ・剣先がちょうど相手に来る距離で」= 一番短い発の剣先が主人公の体の前の縁から 8px 入る所で止まる。
    a.gapCloseBuffer = V6_STOP_BUFFER_PX
    a.preActiveLungeStopGapPx = a.approachDistancePx! - V6_STOP_BUFFER_PX
    // 内側でも 44px 前へ出る決まり (v2 の preActiveLungeMinPx) を外す。近くで連撃を始めるたびに体へ食い込み、
    //   2回目で間が 94→50px、3回目で 43px (体の芯どうしが触れる所) まで詰まっていた (10-21 測定)。
    a.preActiveLungeMinPx = undefined
  }
}
// 6回目 (2026-10-22 作者の答え「周りはC,決めはB」)。記録 = docs/research/2026-10-22-blade-dancer-waltz-c-b.md
//   円舞連斬の絵だけを、ワルツの三拍子で回る形 (C) と、2本を頭上で X 字に交差させる決め (B) に描き直した (紙 3枚・12コマ・m1022_w7_<発><コマ>)。
//   当たる時刻・技の長さ・当たりの前端は v6 のまま (遊んだ手応えを変えずに見た目だけ比べる)。剣は立ち姿の長さにそろえた。
//   10-23 作者「これで配布版へ」で配布版の既定。前の絵は開発版の ?bladeWaltz=v6 (main.ts・boss_motion_lab_entry.ts)。
const W7 = (k: string) => `m1022_w7_${k}`
// 当たる絵の剣先 (画面の px・前 / 床から上): a3 (125, 29)・b3 (138, 142)・c3 (119, 98)。
const W7_ARC_A3 = { anchorFwdPx: 10, anchorUpPx: 110, radiusPx: 140.5, angleFromRad: 1.0, angleToRad: -0.614,
  petals: { spread: 'down' as const, count: 10, seed: 1, sizePx: 22 } }   // 頭の斜め上から足元の前へ、袈裟に斬り下ろす
// 2・3連撃目は 10-23 作者「2,3連撃目のエフェクトが短いな。長さを切っ先の軌道通り出して」で、溜めの絵の切っ先から当たる絵の切っ先までを結ぶ弧にした。
//   測った切っ先: b2 (-35, 152) → b3 (138, 142)・c2 の頭上の剣 (-52, 254) → c3 (119, 98)。記録 = docs/research/2026-10-23-blade-dancer-trail-length-color.md
//   当たりの前端 (v6 の b3 128・c3 110) は変えず、跡だけ本当の切っ先まで出す。trailHeadStart で振り始めの側も細く暗くしすぎない。
const W7_ARC_B3 = { anchorFwdPx: 64, anchorUpPx: 370, radiusPx: 239.6, angleFromRad: -1.9969, angleToRad: -1.2569, trailHeadStart: 0.35,
  petals: { spread: 'side' as const, count: 11, seed: 2, sizePx: 24 } }   // 後ろから肩の高さを平らに払う (大きな半径の弧の底)
const W7_ARC_C3 = { anchorFwdPx: 10, anchorUpPx: 150, radiusPx: 120.8, angleFromRad: 2.111, angleToRad: -0.445, widthPx: 7, trailHeadStart: 0.35,
  petals: { spread: 'side' as const, count: 12, seed: 3, sizePx: 27 } }   // 頭上に振りかぶった剣を、前を回して胸の下まで振り下ろす
// 前の版 (10-22〜10-23)。b3・c3 を当たりの前端で止め、c3 は低い所からの斬り上げだった。開発版 ?bladeTrailLen=now。
const W7_ARC_B3_NOW = { ...W7_ARC_B3, anchorFwdPx: 20, anchorUpPx: 353, radiusPx: 240, angleFromRad: -1.9, angleToRad: -1.095, trailHeadStart: undefined }
const W7_ARC_C3_NOW = { ...W7_ARC_C3, anchorFwdPx: 10, anchorUpPx: 150, radiusPx: 117, angleFromRad: -1.6, angleToRad: -0.498, trailHeadStart: undefined }
// 剣先の通り道。終わりが当たる絵の剣先に乗る (測り方は v6 と同じ)。
export const BLADE_DANCER_W7_SMEAR: Record<string, ContactSmearArc> = {
  [W7('a3')]: { ...BLADE_SMEAR_BASE, ...W7_ARC_A3 },
  [W7('b3')]: { ...BLADE_SMEAR_BASE, ...W7_ARC_B3 },
  [W7('c3')]: { ...BLADE_SMEAR_BASE, ...W7_ARC_C3 },
}
export const BLADE_DANCER_W7_SMEAR_NOW: Record<string, ContactSmearArc> = {
  [W7('a3')]: { ...BLADE_SMEAR_BASE, ...W7_ARC_A3 },
  [W7('b3')]: { ...BLADE_SMEAR_BASE, ...W7_ARC_B3_NOW },
  [W7('c3')]: { ...BLADE_SMEAR_BASE, ...W7_ARC_C3_NOW },
}
export type BladeTrailLen = 'measured' | 'now'
export function parseBladeTrailLen(v: string | null): BladeTrailLen {
  return v === 'now' ? 'now' : 'measured'
}
let w7Smear = BLADE_DANCER_W7_SMEAR
/** いま使っている円舞の斬りの跡の弧 (useBladeBodySize が縮める元)。 */
export function bladeDancerW7Smear(): Record<string, ContactSmearArc> { return w7Smear }
export const BLADE_DANCER_WALTZ_V7_TIMELINE: BossAttackTimeline = withDissolves([
  { phase: 'telegraph', at: 0, image: SU(2), hold: true, tag: 'anticipation', soft: true },
  { phase: 'telegraph', at: 0.07, image: WU(1), hold: true, soft: true },
  { phase: 'telegraph', at: 0.17, image: RAISE, hold: true, soft: true },
  { phase: 'telegraph', at: 0.25, image: WU(2), hold: true, soft: true },
  { phase: 'telegraph', at: 0.38, image: W7('a1'), hold: true, ease: 'easeIn' },   // 1拍目の前・つま先で伸び上がり、手前の剣を頭の斜め上へ
  { phase: 'telegraph', at: 0.85, image: W7('a1'), hold: true, ...TENSE },
  { phase: 'telegraph', at: 0.86, image: W7('a2'), hold: true },                   // 一歩目へ落ちる
  { phase: 'active', at: 0, image: W7('a3'), hold: true, tag: 'contact', hitIndex: 0 },   // 踏み込んで袈裟に斬り下ろす
  { phase: 'comboGap', at: 0, image: W7('a4'), hold: true, tag: 'followThrough', hitIndex: 0 },   // 振り抜いて回り出す
  { phase: 'comboGap', at: 0.5, image: W7('b1'), hold: true, hitIndex: 0 },                        // 2拍目・半回転して背中を見せる
  { phase: 'comboGap', at: 0.72, image: W7('b2'), hold: true, hitIndex: 0 },                       // 回り戻りながら剣を寝かせて溜める
  { phase: 'active', at: 0, image: W7('b3'), hold: true, tag: 'contact', hitIndex: 1 },   // 3拍目・肩の高さを横に払う
  { phase: 'comboGap', at: 0, image: W7('b4'), hold: true, tag: 'followThrough', hitIndex: 1 },   // 払い抜けて上半身がねじれる
  { phase: 'comboGap', at: 0.5, image: W7('c1'), hold: true, hitIndex: 1 },                        // 胸の前で2本を X 字に引き付ける
  { phase: 'comboGap', at: 0.75, image: W7('c2'), hold: true, hitIndex: 1 },                       // 手前の剣を頭上へ振りかぶる
  { phase: 'active', at: 0, image: W7('c3'), hold: true, tag: 'contact', hitIndex: 2 },   // 踏み込んで胸の高さで交差させて斬る
  { phase: 'recovery', at: 0, image: W7('c4'), hold: true, tag: 'followThrough' },                 // 決め・つま先で立ち、頭上で X 字に交差させて止まる
  { phase: 'recovery', at: 0.3, image: W7('c4'), hold: true, tag: 'recover', rot: -2, soft: true },
  { phase: 'recovery', at: 0.42, image: SU(1), hold: true, soft: true },
  { phase: 'recovery', at: 0.56, image: SU(2), hold: true, soft: true },
  { phase: 'recovery', at: 0.7, image: STAND, hold: true, soft: true },
  { phase: 'recovery', at: 0.84, image: 'idle', hold: true },
], { telegraph: 800, recovery: 720 })
/** 円舞連斬だけを v7 の絵にする (useBladeDancerMotionV6 の後に呼ぶ)。当たりの前端は v6 のまま。 */
export function useBladeDancerWaltzV7(trail: BladeTrailStyle = 'afterimage', len: BladeTrailLen = 'measured'): void {
  BOSS_ATTACK_TIMELINES[BOSS_ID] = { ...BOSS_ATTACK_TIMELINES[BOSS_ID], waltzFlurry: BLADE_DANCER_WALTZ_V7_TIMELINE }
  w7Smear = len === 'now' ? BLADE_DANCER_W7_SMEAR_NOW : BLADE_DANCER_W7_SMEAR
  Object.assign(SMEAR_ARTS, bladeDancerV6Smear(trail, w7Smear))
  if (trail === 'petals') preloadBladePetals()
}
// テンポを手本 (Y2tz) に寄せた版 (2026-10-24 比較版 → 同日 作者の答え Y-1「比較版を配布版にする (円舞も終舞も)」で配布版の既定・前のテンポは DEV ?bladeTempo=old)。記録 = docs/research/2026-10-24-blade-dancer-tempo-y2tz.md
//   作者「これだった。剣舞のテンポの手本にして」= 10-17 の答えるページ「剣舞の技の動き」(Artifact Y2tz) の見本の拍に、円舞と終舞の時間を合わせる。
//   見本の一拍は「振り出し + 当たり + 抜け」の全体 (円舞 380・終舞 300)。配布版は当たり (activeMs) のあとに一拍ぶんの間 (comboGapMs) を
//   置いていたので、一拍が 510 / 420ms に延びていた。ここでは 間 = 一拍 − 当たり にする。弾いた時の止まり (hits.ts) は全ボス共通なので触らない。
//   絵は配布版 (v6・v7) の絵を使い回す。見本にある息の絵・刃を収める絵・息の間の暗器は配布版に無いので入れない。
// 当たりから次の当たりへ移る所で、ゲームは1コマ (約17ms) 遅れる (撮って測った)。見本の拍で当たるよう、間からそのぶんを先に引く。
const Y2TZ_TICK = 16
const Y2TZ_WALTZ = { chargeMs: 520, comboGapMs: 380 - 130 - Y2TZ_TICK, recoveryMs: 250 + 700 }
// 終舞 = 4連 → 息 → 5連 → 息 → 2連 → 大振り → 肩で息。連の中の一拍 300・息 480・大振りの振り出し 200 (見本 e3Scene)。
const Y2TZ_CRES_CHAIN_GAP = 300 - 120 - Y2TZ_TICK
const Y2TZ_CRES_BREATH_GAP = 120 + 480 + 60 - Y2TZ_TICK
const Y2TZ_CRES_BIG_GAP = 120 + 200 - Y2TZ_TICK
export const BLADE_DANCER_Y2TZ_CRESCENDO_GAPS_MS = [
  ...Array(3).fill(Y2TZ_CRES_CHAIN_GAP), Y2TZ_CRES_BREATH_GAP,
  ...Array(4).fill(Y2TZ_CRES_CHAIN_GAP), Y2TZ_CRES_BREATH_GAP,
  Y2TZ_CRES_CHAIN_GAP, Y2TZ_CRES_BIG_GAP,
] as number[]
// 見本は構え 900 + 振り出し 60 で1発目。戻りは 抜け 200 + 肩で息 600 に、大振りの当たり (見本 150・ここは 120 のまま) の差 30 を足す。
// postureDamage は当たった時に主人公の体幹へ溜まる量。全部受けた時の合計を5連 (20 × 5 = 100) と近く (8 × 12 = 96) する。
// parryPostureMul は弾いた時にボスへ溜まる体幹の倍率 (10-24 配布版にした時に詰めた・docs/research/2026-10-24-blade-dancer-crescendo-posture.md)。
//   連続弾きの倍率 (hits.ts・5段で ×2) が12発に掛かり、そのままだと空の体幹から弾いても8〜9発目で崩れ、終舞の後半が見えない。
//   0.5 = 空から12発を弾き切ると体幹の約74%・4割溜まっていれば11発目 (最後の2連) で崩れる (騎士の五連の 0.65 と同じ考え＝大きな報酬だが勝手には崩さない)。
const Y2TZ_CRES = { chargeMs: 900 + 60, recoveryMs: 200 + 600 + 30, comboHits: 12, postureDamage: 8, parryPostureMul: 0.5 }
// 発ごとの絵 (当たる絵・その前の絵・振り抜いた絵)。v6 の5連の5発を回して12発にする。
const Y2TZ_STRIKE = {
  d2: { pre: 'd1', ft: 'd3' }, d4: { pre: 'd3', ft: 'e1' }, e3: { pre: 'e2', ft: 'e4' }, f2: { pre: 'f1', ft: 'f3' }, g1: { pre: 'f4', ft: 'g2' },
} as const
type Y2tzStrike = keyof typeof Y2TZ_STRIKE
export const BLADE_DANCER_Y2TZ_CRESCENDO_STRIKES: Y2tzStrike[] = ['d2', 'd4', 'e3', 'f2', 'd2', 'd4', 'e3', 'f2', 'd2', 'd4', 'e3', 'g1']
function y2tzCrescendoGapKeys(i: number): SoftKey[] {
  const now = Y2TZ_STRIKE[BLADE_DANCER_Y2TZ_CRESCENDO_STRIKES[i]!]
  const next = Y2TZ_STRIKE[BLADE_DANCER_Y2TZ_CRESCENDO_STRIKES[i + 1]!]
  const gap = BLADE_DANCER_Y2TZ_CRESCENDO_GAPS_MS[i]!
  const ft: SoftKey = { phase: 'comboGap', at: 0, image: V(now.ft), hold: true, tag: 'followThrough', hitIndex: i }
  if (now.ft === next.pre) return [ft]
  if (gap === Y2TZ_CRES_BREATH_GAP) return [   // 息: 振り抜いて、立ち姿の手前で息をつき、次の連の振り出しへ
    { ...ft, soft: true },
    { phase: 'comboGap', at: 0.2, image: STAND, hold: true, hitIndex: i, soft: true },
    { phase: 'comboGap', at: 0.85, image: V(next.pre), hold: true, hitIndex: i },
  ]
  return [ft, { phase: 'comboGap', at: gap === Y2TZ_CRES_BIG_GAP ? 0.4 : 0.5, image: V(next.pre), hold: true, hitIndex: i }]
}
export const BLADE_DANCER_Y2TZ_TIMELINES: Record<string, BossAttackTimeline> = {
  // 円舞: 時刻の割合は v7 のまま。戻りの最後に、刃を持ち替える絵 (swap_draw・10-19 の技ごとの絵) を挟む。
  waltzFlurry: withDissolves([
    ...BLADE_DANCER_WALTZ_V7_TIMELINE.filter(k => k.phase !== 'recovery' && k.hold),
    { phase: 'recovery', at: 0, image: W7('c4'), hold: true, tag: 'followThrough' },
    { phase: 'recovery', at: 0.18, image: W7('c4'), hold: true, tag: 'recover', rot: -2, soft: true },
    { phase: 'recovery', at: 0.27, image: 'swap_draw', hold: true, soft: true },   // 刃を持ち替える (見本 swap 700ms)
    { phase: 'recovery', at: 0.68, image: STAND, hold: true, soft: true },
    { phase: 'recovery', at: 0.84, image: 'idle', hold: true },
  ], { telegraph: Y2TZ_WALTZ.chargeMs, recovery: Y2TZ_WALTZ.recoveryMs }),
  crescendo: withDissolves([
    ...BLADE_DANCER_MOTION_V6_TIMELINES.crescendo!.filter(k => k.phase === 'telegraph' && k.hold),
    ...BLADE_DANCER_Y2TZ_CRESCENDO_STRIKES.flatMap((s, i): SoftKey[] => [
      { phase: 'active', at: 0, image: V(s), hold: true, tag: 'contact', hitIndex: i },
      ...(i < BLADE_DANCER_Y2TZ_CRESCENDO_STRIKES.length - 1 ? y2tzCrescendoGapKeys(i) : []),
    ]),
    { phase: 'recovery', at: 0, image: V('g2'), hold: true, tag: 'followThrough', soft: true },   // 剣先が床近くまで下りる
    { phase: 'recovery', at: 0.25, image: V('g3'), hold: true, tag: 'recover', soft: true },      // 肩で息をする (見本 600ms)
    { phase: 'recovery', at: 0.75, image: STAND, hold: true, soft: true },
    { phase: 'recovery', at: 0.9, image: 'idle', hold: true },
  ], { telegraph: Y2TZ_CRES.chargeMs, recovery: Y2TZ_CRES.recoveryMs }),
}
/** テンポを手本 (Y2tz) に合わせる (配布版の既定・DEV ?bladeTempo=old の時だけ呼ばない・useBladeDancerWaltzV7 の後に呼ぶ)。 */
export function useBladeDancerTempoY2tz(): void {
  BOSS_ATTACK_TIMELINES[BOSS_ID] = { ...BOSS_ATTACK_TIMELINES[BOSS_ID], ...BLADE_DANCER_Y2TZ_TIMELINES }
  Object.assign(ATTACKS.waltzFlurry!, Y2TZ_WALTZ)
  const c = ATTACKS.crescendo!
  Object.assign(c, Y2TZ_CRES, { comboGapsMs: BLADE_DANCER_Y2TZ_CRESCENDO_GAPS_MS })
  // 当たりの前端は、5連の時に発ごとの絵で決めた段 (体の大きさの補正込み) を12発へ並べ直す。
  const five = c.comboStages!
  const order: Y2tzStrike[] = ['d2', 'd4', 'e3', 'f2', 'g1']
  c.comboStages = BLADE_DANCER_Y2TZ_CRESCENDO_STRIKES.map(s => ({ ...five[order.indexOf(s)]! }))
  if (c.comboLunge) c.comboLunge = { ...c.comboLunge, followHits: 12, plantFromHit: 12 }
}
// 溜めの最後で寄る長さ (ms) = 踏み出す絵 (2) が出ている時間 + 少し前から (振りかぶりの頂点で沈んでから出る)。
const V2_STEP_IN: Record<string, { ms: number }> = { waltzFlurry: { ms: 180 }, crescendo: { ms: 180 } }
// 届く間合いより少し内側まで寄る (剣先がちゃんと相手に掛かる)。連撃の間の寄りの止まり (approach - gapCloseBuffer 28) と同じにする
//   = 溜めで寄り終えた所から、連撃の間にさらに 8px 滑ることが無い。
const V2_STEP_IN_SHORT_PX = 28
// 近くで始めても踏み出す絵の間にこれだけは前へ出る (足踏みに見えない)。主人公の体の手前で止まる。
const V2_STEP_IN_MIN_PX = 44
// 連撃の間に滑って寄る速さの上限 (px/s)。主人公の歩き (120px/s 前後) より速く、配布版の踏み込み (1900) よりずっと遅い。
const V2_GLIDE_PX_S = 420
// 滑りの入りと止まり。前の振りの抜け (絵 4) から 120ms かけて上限速へ上げ、残り × 8 /s を上限に減速する
//   (420px/s で一気に動き出し、着いた1コマで止まっていた。減速で残る分は 20px ほどで、届く間合いの余り 28px の内)。
const V2_GLIDE_RAMP_MS = 120
const V2_GLIDE_BRAKE_PER_S = 8
